import React, { useState } from "react";
import { SketchPicker } from "react-color";
import { useEditCtaContext } from "../../../../../../context/EditCtaContext";

const ButtonConfigGroup = () => {
  const { formData, setFormData, getCurrentStep } = useEditCtaContext();

  const [showFontColorPicker, setShowFontColorPicker] = useState(false);
  const [showButtonColorPicker, setShowButtonColorPicker] = useState(false);

  const fonts = [
    "Helvetica",
    "Roboto",
    "Montserrat",
    "Arial",
    "Times New Roman",
    "Courier New",
    "Verdana",
    "Georgia",
  ];

  const sizes = Array.from({ length: 300 }, (_, i) => i + 8);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const toggleStyle = (style) => {
    setFormData({ ...formData, [style]: !formData[style] });
  };

  const handleFontColorChange = (color) => {
    setFormData({ ...formData, buttonFontColor: color.hex });
  };

  const handleButtonColorChange = (color) => {
    setFormData({ ...formData, buttonBackgroundColor: color.hex });
  };

  return (
    <div className="flex flex-col px-6 pb-6">
      {/* Step Header */}
      <div className="flex flex-col pt-4 mb-6">
        <h2 className="text-white text-2xl font-semibold leading-[30px] tracking-[-0.15px] mb-0">
          Step {getCurrentStep() + 1}
        </h2>
        <p className="text-white text-xl font-medium leading-6">
          Configure your button
        </p>
      </div>

      {/* Form Fields */}
      <div className="flex flex-col gap-3">
        {/* Label Input */}
        <div className="flex flex-col gap-1">
          <label className="text-white text-base font-bold leading-[22px] tracking-[-0.18px]">
            Label
          </label>
          <input
            type="text"
            placeholder="Get started"
            value={formData.buttonLabel || ""}
            onChange={(e) => handleChange("buttonLabel", e.target.value)}
            className="h-9 px-3 py-2 rounded border border-[#DCDFE3] bg-white text-base text-gray-700 placeholder:text-[#758195] focus:outline-none focus:ring-1 focus:ring-purple-500"
          />
        </div>

        {/* Font Selector and Style Buttons */}
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-1">
            <label className="text-white text-base font-bold leading-[22px] tracking-[-0.18px]">
              Font
            </label>
            <div className="relative">
              <select
                value={formData.buttonFont || "Helvetica"}
                onChange={(e) => handleChange("buttonFont", e.target.value)}
                className="h-9 w-full px-3 py-2 rounded border border-[#DCDFE3] bg-white text-base text-[#758195] appearance-none focus:outline-none focus:ring-1 focus:ring-purple-500"
              >
                {fonts.map((font) => (
                  <option key={font} value={font}>
                    {font}
                  </option>
                ))}
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <svg width="8" height="22" viewBox="0 0 8 22" fill="none">
                  <path
                    d="M0 7.02468L3.84664 0.200195L8 7.00454L0 7.02468Z"
                    fill="#231F20"
                  />
                  <path
                    d="M8 14.376L4.15336 21.2005L0 14.3962L8 14.376Z"
                    fill="#231F20"
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* Text Style Buttons */}
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => toggleStyle("buttonIsBold")}
              className={`w-[22px] h-[22px] flex items-center justify-center rounded transition-opacity ${
                formData.buttonIsBold ? "opacity-100" : "opacity-60"
              }`}
            >
              <svg width="23" height="23" viewBox="0 0 23 23" fill="none">
                <path
                  d="M18.7802 0.46875H3.79016C1.87926 0.46875 0.330078 2.01781 0.330078 3.92871V18.9187C0.330078 20.8296 1.87926 22.3787 3.79016 22.3787H18.7802C20.6911 22.3787 22.2401 20.8296 22.2401 18.9187V3.92871C22.2401 2.01781 20.6911 0.46875 18.7802 0.46875Z"
                  fill="#9492AD"
                  stroke="white"
                  strokeWidth="0.5"
                />
                <text
                  x="6"
                  y="16"
                  fill="white"
                  fontSize="13"
                  fontWeight="700"
                  fontFamily="Montserrat, sans-serif"
                >
                  B
                </text>
              </svg>
            </button>

            <button
              onClick={() => toggleStyle("buttonIsItalic")}
              className={`w-[22px] h-[22px] flex items-center justify-center rounded transition-opacity ${
                formData.buttonIsItalic ? "opacity-100" : "opacity-60"
              }`}
            >
              <svg width="23" height="23" viewBox="0 0 23 23" fill="none">
                <path
                  d="M18.7996 0.359375H3.80957C1.89867 0.359375 0.349609 1.90843 0.349609 3.81934V18.8093C0.349609 20.7202 1.89867 22.2693 3.80957 22.2693H18.7996C20.7105 22.2693 22.2596 20.7202 22.2596 18.8093V3.81934C22.2596 1.90843 20.7105 0.359375 18.7996 0.359375Z"
                  fill="#9492AD"
                  stroke="white"
                  strokeWidth="0.5"
                />
                <text
                  x="9"
                  y="16"
                  fill="white"
                  fontSize="13"
                  fontStyle="italic"
                  fontFamily="Montserrat, sans-serif"
                >
                  I
                </text>
              </svg>
            </button>

            <button
              onClick={() => toggleStyle("buttonIsUnderline")}
              className={`w-[22px] h-[22px] flex items-center justify-center rounded transition-opacity ${
                formData.buttonIsUnderline ? "opacity-100" : "opacity-60"
              }`}
            >
              <svg width="23" height="23" viewBox="0 0 23 23" fill="none">
                <path
                  d="M18.8213 0.249023H3.83105C1.92015 0.249023 0.371094 1.79808 0.371094 3.70898V18.6992C0.371094 20.6101 1.92015 22.1592 3.83105 22.1592H18.8213C20.7322 22.1592 22.2812 20.6101 22.2812 18.6992V3.70898C22.2812 1.79808 20.7322 0.249023 18.8213 0.249023Z"
                  fill="#9492AD"
                  stroke="white"
                  strokeWidth="0.5"
                />
                <path
                  d="M16.6211 8.54991C16.6211 8.60991 16.6211 8.74968 16.6211 8.80968C16.6011 8.94968 16.5114 9.15989 16.4414 9.27989L13.4717 12.1695L10.6816 9.35997L13.7217 6.3795C14.3117 6.0295 15.0014 6.46954 15.4414 6.91954C15.7514 7.23954 16.5909 8.11991 16.6309 8.54991H16.6211Z"
                  fill="white"
                />
                <path
                  d="M6.65234 15.04V14.9102L6.72266 14.8003L7.91211 13.6299L9.20215 14.9302C9.08215 15.0702 8.94219 15.2803 8.74219 15.2803C8.16219 15.2403 7.51238 15.3403 6.94238 15.2803C6.78238 15.2603 6.71234 15.1903 6.65234 15.0503V15.04Z"
                  fill="white"
                />
                <path
                  d="M10.4512 10.5801L12.291 12.4302C11.741 12.9302 10.9008 13.6599 10.5908 13.8799C10.5508 13.8799 10.4916 13.8704 10.4316 13.8604C10.3216 13.8404 10.1912 13.8203 10.0312 13.8203H10.001C9.96098 13.8203 9.91109 13.8203 9.87109 13.8203L9.01074 12.96C9.01074 12.85 9.01074 12.7499 9.01074 12.6299C9.01074 12.5599 8.99145 12.4899 8.98145 12.4199C9.19145 12.0699 9.91094 11.2001 10.4609 10.5801M10.4316 9.62012C10.1816 9.88012 8.28078 11.9904 8.30078 12.3604C8.30078 12.4704 8.35133 12.5802 8.36133 12.6802C8.37133 12.8402 8.36102 13.0102 8.29102 13.1602L9.66113 14.54C9.78113 14.52 9.89074 14.4702 10.0107 14.4702H10.0312C10.2313 14.4702 10.4013 14.54 10.6113 14.54C10.6613 14.54 10.7215 14.5403 10.7715 14.5303C11.0115 14.4903 13.0514 12.63 13.2314 12.46L10.4209 9.62988L10.4316 9.62012Z"
                  fill="white"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Link Input */}
        <div className="flex flex-col gap-1">
          <label className="text-white text-base font-bold leading-[22px] tracking-[-0.18px]">
            Link
          </label>
          <input
            type="text"
            placeholder="https://"
            value={formData.buttonLink || ""}
            onChange={(e) => handleChange("buttonLink", e.target.value)}
            className="h-9 px-3 py-2 rounded border border-[#DCDFE3] bg-white text-base text-gray-700 placeholder:text-[#758195] focus:outline-none focus:ring-1 focus:ring-purple-500"
          />
        </div>

        {/* Font Size and Button Size */}
        <div className="flex items-start gap-[54px]">
          <div className="flex flex-col gap-1 w-[83px]">
            <label className="text-white text-base font-bold leading-[22px] tracking-[-0.18px]">
              Font Size
            </label>
            <div className="relative">
              <select
                value={formData.buttonFontSize || "23"}
                onChange={(e) => handleChange("buttonFontSize", e.target.value)}
                className="h-9 w-full px-3 py-2 rounded border border-[#DCDFE3] bg-white text-base text-[#758195] appearance-none focus:outline-none focus:ring-1 focus:ring-purple-500"
              >
                {sizes.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <svg width="8" height="22" viewBox="0 0 8 22" fill="none">
                  <path
                    d="M0 7.13503L3.84664 0.310547L8 7.11489L0 7.13503Z"
                    fill="#231F20"
                  />
                  <path
                    d="M8 14.4863L4.15336 21.3108L0 14.5066L8 14.4863Z"
                    fill="#231F20"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-white text-base font-bold leading-[22px] tracking-[-0.18px]">
              Button Size
            </label>
            <div className="relative">
              <select
                value={formData.buttonSize || "23"}
                onChange={(e) => handleChange("buttonSize", e.target.value)}
                className="h-9 w-full px-3 py-2 rounded border border-[#DCDFE3] bg-white text-base text-[#758195] appearance-none focus:outline-none focus:ring-1 focus:ring-purple-500"
              >
                {sizes.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <svg width="8" height="22" viewBox="0 0 8 22" fill="none">
                  <path
                    d="M0 7.13503L3.84664 0.310547L8 7.11489L0 7.13503Z"
                    fill="#231F20"
                  />
                  <path
                    d="M8 14.4863L4.15336 21.3108L0 14.5066L8 14.4863Z"
                    fill="#231F20"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Font Color and Button Color */}
        <div className="flex items-start gap-[54px]">
          <div className="flex flex-col gap-1">
            <label className="text-white text-base font-bold leading-[22px] tracking-[-0.18px]">
              Font Color
            </label>
            <div className="relative">
              <div
                onClick={() => setShowFontColorPicker(!showFontColorPicker)}
                className="w-11 h-11 rounded border border-white cursor-pointer"
                style={{
                  backgroundColor: formData.buttonFontColor || "#D8D8D8",
                }}
              />
              {showFontColorPicker && (
                <div className="absolute z-10 -mt-96">
                  <div
                    className="fixed inset-0"
                    onClick={() => setShowFontColorPicker(false)}
                  />
                  <SketchPicker
                    color={formData.buttonFontColor || "#D8D8D8"}
                    onChange={handleFontColorChange}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-white text-base font-bold leading-[22px] tracking-[-0.18px]">
              Button Color
            </label>
            <div className="relative">
              <div
                onClick={() => setShowButtonColorPicker(!showButtonColorPicker)}
                className="w-11 h-11 rounded border border-white cursor-pointer"
                style={{
                  backgroundColor: formData.buttonBackgroundColor || "#0BA007",
                }}
              />
              {showButtonColorPicker && (
                <div className="absolute z-10 -mt-96">
                  <div
                    className="fixed inset-0"
                    onClick={() => setShowButtonColorPicker(false)}
                  />
                  <SketchPicker
                    color={formData.buttonBackgroundColor || "#0BA007"}
                    onChange={handleButtonColorChange}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ButtonConfigGroup;
